/**
 * @fileoverview A function that formats the score of a participant.
 *
 * @example
 * formatScore(100, 'A')
 */

import { Tooltip } from 'react-tooltip'
import { v4 as uuid } from 'uuid'
import { formatOutput } from './formatOutput'

/**
 * @function formatScore
 * @description Formats the score of a participant.
 * @param {number} score - The score of the participant.
 * @param {string} level - The level of the participant.
 * @returns {JSX.Element} - The formatted score.
 */
export function formatScore(score: number, level?: string) {
    if (score === undefined) return formatOutput(score)
    const id = uuid()

    let scoreColor = 'bg-indigo-500'
    let textColor = 'text-white'

    if (score > 47) {
        scoreColor = 'bg-indigo-100'
        textColor = 'text-indigo-700'
    }

    if (score > 53) {
        scoreColor = 'bg-gray-200'
        textColor = 'text-gray-600'
    }

    return (
        <>
            <div className="flex" id={`score-${id}`}>
                <span
                    className={`${scoreColor} ${textColor} flex text-xs font-medium rounded-md py-0.5 w-6 justify-center`}
                >
                    {Math.round(score)}
                </span>
                {level && (
                    <span className="pl-1 whitespace-nowrap text-sm font-medium">
                        {level.slice(0, 1).toUpperCase() + level.slice(1).toLocaleLowerCase()}
                    </span>
                )}
            </div>
            <Tooltip clickable place="left" anchorSelect={`#score-${id}`}>
                <div className="w-40 whitespace-break-spaces">
                    <span className="text-xs">Score on the Novoic test of verbal recall.</span>
                    <p>
                        <a
                            className="text-xs text-indigo-400 underline underline-offset-2"
                            href="https://help.novoic.com/en/articles/8489985-what-do-the-scores-mean-how-do-i-interpret-the-score"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Learn more →
                        </a>
                    </p>
                </div>
            </Tooltip>
        </>
    )
}
